import { Service, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import { environment } from '../../environments/environment';
import apiContractSnapshot from '../data/api-contract-snapshot.json';
import { type OpenApiDocument } from '../data/openapi-types';
import { endpointGroupNames, parseOpenApiDocument } from '../data/parse-openapi';

// The snapshot is a plain JSON import; its inferred type is structurally compatible but wider.
const SNAPSHOT_DOCUMENT = apiContractSnapshot as OpenApiDocument;

/**
 * Provides the API reference endpoints, parsed from the live OpenAPI document.
 *
 * The vendored contract snapshot renders immediately (and during prerendering); once the live
 * document is fetched in the browser it takes over, so backend contract changes show up without
 * redeploying the portal. On fetch errors the snapshot stays in place.
 */
@Service()
export class ApiSpecService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly liveDocument = signal<OpenApiDocument | null>(null);

  readonly endpoints = computed(() =>
    parseOpenApiDocument(this.liveDocument() ?? SNAPSHOT_DOCUMENT),
  );

  readonly groupNames = computed(() => endpointGroupNames(this.endpoints()));

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.http
        .get<OpenApiDocument>(environment.swaggerUrl)
        .pipe(
          takeUntilDestroyed(),
          catchError(() => of(null)),
        )
        .subscribe((document) => {
          if (document?.paths) {
            this.liveDocument.set(document);
          }
        });
    }
  }
}
